import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { minutesSince, receptionSummary, takeReceptionVisit, useReceptionToday } from '../utils/reception';
import type { ReceptionVisit } from '../utils/reception';

const GOLD = '#9d6f32';
const DARK = '#1c1b19';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/** Office only: a reception alert for admins, without CRM chats or follow-ups. */
export default function OfficeReceptionAlert() {
  const { currentUser } = useAuth();
  const visits = useReceptionToday(db);
  const waiting = useMemo(() => visits.filter((visit) => visit.status === 'waiting'), [visits]);
  const [expanded, setExpanded] = useState(false);
  const [takingId, setTakingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const fresh = waiting.filter((visit) => !seenIds.current.has(visit.id));
    if (fresh.length) {
      fresh.forEach((visit) => seenIds.current.add(visit.id));
      setExpanded(true);
    }
    if (!waiting.length) {
      setExpanded(false);
      setError(null);
    }
  }, [waiting]);

  const take = async (visit: ReceptionVisit) => {
    if (!currentUser?.email) return;
    setTakingId(visit.id);
    setError(null);
    try {
      await takeReceptionVisit(db, visit, {
        email: currentUser.email,
        name: currentUser.displayName || currentUser.email,
      });
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setTakingId(null);
    }
  };

  if (!currentUser || waiting.length === 0) return null;

  return <Box sx={{ position: 'fixed', right: { xs: 12, sm: 20 }, bottom: 20, zIndex: 1300, width: expanded ? { xs: 'calc(100vw - 24px)', sm: 440 } : 'auto', maxWidth: 'calc(100vw - 24px)', fontFamily: FONT }}>
    {expanded ? <Paper elevation={8} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e8e3da', maxHeight: 'min(65vh, 600px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ bgcolor: DARK, color: '#fff', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsActiveOutlinedIcon sx={{ fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>My Today</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#d7d7d7' }}>{waiting.length} waiting at reception</Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close My Today" onClick={() => setExpanded(false)}
          sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: 0, borderRadius: '50%', bgcolor: 'transparent', color: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#3b3b3b' } }}>
          <CloseIcon sx={{ fontSize: 19 }} />
        </Box>
      </Box>
      <Box sx={{ overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#fffaf3' }}>
        {waiting.map((visit) => {
          const wait = minutesSince(visit.checkedInAt, now);
          return <Box key={visit.id} sx={{ bgcolor: '#fff', border: '1px solid #f0c98f', borderRadius: 1.5, p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
              <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 14, fontWeight: 700 }}>{visit.name}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: wait >= 10 ? '#b3261e' : '#a35f00' }}>{wait} min</Typography>
            </Box>
            <Typography sx={{ mt: 0.5, fontFamily: FONT, fontSize: 12, color: '#4c4c4c' }}>{receptionSummary(visit)}</Typography>
            {(visit.ownerName || visit.ownerEmail) && <Typography sx={{ mt: 0.25, fontFamily: FONT, fontSize: 12, color: '#666' }}>Assigned to {visit.ownerName || visit.ownerEmail}</Typography>}
            <Box component="button" type="button" disabled={takingId === visit.id} onClick={() => take(visit)}
              sx={{ mt: 1.25, minHeight: 34, px: 1.75, borderRadius: 999, border: 0, bgcolor: GOLD, color: '#fff', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', '&:disabled': { opacity: 0.5, cursor: 'default' } }}>
              {takingId === visit.id ? 'Taking…' : "I'll take it"}
            </Box>
          </Box>;
        })}
        {error && <Typography role="alert" sx={{ color: '#b3261e', fontFamily: FONT, fontSize: 12 }}>{error}</Typography>}
      </Box>
    </Paper> : <Box component="button" type="button" aria-label={`My Today, ${waiting.length} waiting at reception`} onClick={() => setExpanded(true)}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40, px: 1.75, border: 0, borderRadius: 999, bgcolor: DARK, color: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,.25)', fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
      <NotificationsActiveOutlinedIcon sx={{ fontSize: 18 }} />
      My Today
      <Box component="span" sx={{ px: 0.9, borderRadius: 999, bgcolor: GOLD, fontSize: 11.5, lineHeight: '20px' }}>{waiting.length} waiting</Box>
    </Box>}
  </Box>;
}
